import { useEffect, useRef, useState } from 'react'

import { useFilePreview } from '@/api/models/requests/hooks/useFilePreview'
import { FileType } from '@/api/models/requests/lib/requests.types'
import { UploadedFileType } from '@/components/shared/file-uploader'

import { UploadProgress } from '../model/constants'

type Params = {
  defaultFiles?: UploadedFileType[]
  onUpload?: (files: UploadedFileType[]) => void
  multiple?: boolean
  setUploadProgress?: (progress: number) => void
  resetKey?: string | number
}

export const useUploadFiles = ({
  defaultFiles,
  onUpload,
  multiple = true,
  setUploadProgress,
  resetKey
}: Params) => {
  const [files, setFiles] = useState<UploadedFileType[]>(defaultFiles ?? [])

  const hasReportedFiles = useRef(false)
  const onUploadRef = useRef(onUpload)

  const preview = useFilePreview(files, FileType.Local)

  useEffect(() => {
    onUploadRef.current = onUpload
  }, [onUpload])

  useEffect(() => {
    if (!defaultFiles?.length) {
      return
    }

    setFiles(prev => {
      const isSame =
        prev.length === defaultFiles.length &&
        prev.every((file, index) => file.id === defaultFiles[index]?.id)

      return isSame ? prev : defaultFiles
    })
  }, [defaultFiles])

  useEffect(() => {
    if (resetKey === undefined) {
      return
    }

    setFiles([])
    hasReportedFiles.current = false
  }, [resetKey])

  useEffect(() => {
    if (!hasReportedFiles.current && files.length === 0) {
      return
    }

    hasReportedFiles.current = true
    onUploadRef.current?.(files)
  }, [files])

  const handleUpload = (data: UploadedFileType) => {
    if (!data.file) {
      return
    }

    if (multiple) {
      onUpload?.([...files, data])
    } else {
      onUpload?.([data])
    }
  }

  const onReadFile = (data: UploadedFileType) => {
    setFiles(prev => {
      const fileIndex = prev.findIndex(prevFile => prevFile.id === data.id)

      if (fileIndex >= 0) {
        const _prev = [...prev]
        _prev[fileIndex] = data
        return _prev
      }

      return [...prev, data]
    })
  }

  const handleRead = (data: UploadedFileType) => {
    setUploadProgress?.(data.progress)

    if (
      data.progress === UploadProgress.Start ||
      data.progress === UploadProgress.Complete ||
      !data.loading
    ) {
      onReadFile(data)
    }
  }

  const handleDeleteFile = (id: string) => {
    setFiles(prev => {
      const fileIndex = prev.findIndex(prevFile => prevFile.id === id)

      if (fileIndex >= 0) {
        const _prev = [...prev]
        _prev.splice(fileIndex, 1)
        return _prev
      }

      return prev
    })
  }

  return {
    files,
    handleUpload,
    handleRead,
    handleDeleteFile,
    preview
  }
}
